import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBrand } from '../brand';
import { useCopy } from '../copy';
import { BrandMascot } from '../BrandMascot';
import { GoingBadge, Help, LockIcon, Mark, Seg } from '../primitives';
import { useTrack, useTrackExpand } from '../track';
import { anyWithinSixMinutes, useNow } from '../lib/useNow';
import { money, pct, plural, roiDirection, roiText, localTime, shortDay, titleCase, upcomingClock } from '../lib/format';
import {
  categoryRoi,
  categoryValueRoi,
  dayTiles,
  groupMeetings,
  meetingHref,
  meetingStats,
  racesRun,
  share,
  showValueRoi,
  upcomingHref,
  type DayAgreement,
  type DayTotals,
  type HomeMeeting,
  type UpcomingRow,
  meetingRefHref,
  type MeetingRef,
} from './lib';
import './home.css';

/** A signed ROI figure coloured up/down. */
export function Roi({ value }: { value: number | null | undefined }) {
  return <b className={`ui-home-roi ${roiDirection(value)}`}>{roiText(value)}</b>;
}


/**
 * The next races with a tip, across every track. The brand's first few show;
 * "Next N" reveals the rest of the day. Locked rows hide the pick.
 */
export function UpcomingTips({ rows, locked, signedOut }: { rows: UpcomingRow[]; locked: boolean; signedOut?: boolean }) {
  const { upcomingShown } = useBrand();
  const copy = useCopy();
  const [open, setOpen] = useState(false);
  // tick every second while a race is inside six minutes, so its countdown runs in seconds
  const now = useNow(anyWithinSixMinutes(rows.map((r) => r.time)) ? 1000 : 30000);
  const track = useTrack();
  const expand = useTrackExpand();
  // a race leaves the strip 45 seconds after the jump, on this clock rather than the next poll
  const live = rows.filter((r) => Date.parse(r.time) + 45000 > now);
  if (!live.length) return null;
  const extra = live.length - upcomingShown;
  const shown = open ? live : live.slice(0, upcomingShown);
  return (
    <section className="ui-ut" aria-label={copy.upcomingTitle} data-tour="upcoming">
      <h2>
        {copy.upcomingTitle}
        {extra > 0 && (
          <button
            type="button"
            className="more"
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
              expand('upcoming_more', !open, { extra });
            }}
          >
            {open ? 'Fewer' : `Next ${extra}`}
            <span className="chev" aria-hidden="true">▾</span>
          </button>
        )}
      </h2>
      <div className="rows">
        {shown.map((r) => {
          const clock = upcomingClock(r.time, now);
          const to = signedOut
            ? { pathname: '/register' }
            : upcomingHref(r);
          return (
            <Link
              key={r.id}
              className={`row${clock.urgent ? ' urgent' : ''}`}
              to={to}
              state={signedOut ? { message: 'Sign up to see our racing analysis', returnTo: upcomingHref(r).split('?')[0] } : undefined}
              onClick={() => track('ui_click', { target: 'upcoming_tip', trackName: r.track, raceNumber: r.race, locked, signedOut: !!signedOut })}
            >
              <span className="l1">
                <b>
                  {r.track} R{r.race}
                </b>
                <span className="tm">{localTime(r.time)}</span>
              </span>
              <span className="l2">
                {locked ? (
                  <span className="lock">
                    <LockIcon /> {signedOut ? 'Sign up' : 'Upgrade'}
                  </span>
                ) : !r.number ? (
                  <span className="lock">Tips coming</span>
                ) : (
                  <>
                    <span className="num">{r.number}</span>
                    <span className="hn">{r.horse}</span>
                    <Mark show={r.edge} iconOnly />
                  </>
                )}
                <span className="cd">{clock.text}</span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/** The day's scorecard: one tinted summary row that opens to the full tiles. */
export function TodaySoFar({ totals, roi, valueRoi, label, agree }: { totals: DayTotals; roi: number; valueRoi: number; label: string; agree?: DayAgreement }) {
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState<string | null>(null);
  const expand = useTrackExpand();
  const tiles = dayTiles(totals, agree);
  if (!tiles.length) return null;
  return (
    <div className="ui-tsf" data-open={open} data-tour="sofar">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          expand('today_so_far', !open);
        }}
      >
        <div>
          <div className="k">
            {label} · {totals.meetings} meetings, {totals.races} races
          </div>
          <div className="row">
            <span>
              Top pick won <b>{pct(share(totals.topWinners, totals.races), 0)}</b>{' '}
              <span className="of">
                {totals.topWinners}/{totals.races}
              </span>
            </span>
            <span>
              Top pick ROI <Roi value={roi} />
            </span>
            {showValueRoi(valueRoi) && (
              <span>
                Value ROI <Roi value={valueRoi} />
              </span>
            )}
            {/* Prime Pick / Platinum Edge ROI: hidden for now at the product owner's request (2026-09-30).
                The figure is still served as consensusPickStats.agreeRoi; restore with the TodaySoFar tests
                removed in the same change.
            {agree && agree.races > 0 && agree.roi != null && (
              <span>
                {agree.markName} ROI <Roi value={agree.roi} />
              </span>
            )} */}
          </div>
        </div>
        <span className="exp">
          {open ? 'Less' : 'More stats'} <span className="chev" aria-hidden="true">▾</span>
        </span>
      </button>
      <div className="more" aria-hidden={!open}>
        <div>
          <div className="tiles">
            {tiles.map((t) => (
              <div key={t.key} className="tile" data-open={tip === t.key} style={{ ['--tc' as string]: t.colour }}>
                <span className="th2">{t.label}</span>
                <span className="c">
                  {t.count}/{t.of}
                </span>
                <span className="pc">{pct(share(t.count, t.of))}</span>
                <button
                  type="button"
                  aria-expanded={tip === t.key}
                  aria-label={`What is ${t.label}?`}
                  tabIndex={open ? 0 : -1}
                  onClick={() => {
                    setTip(tip === t.key ? null : t.key);
                    expand('stat_explainer', tip !== t.key, { stat: t.key });
                  }}
                >
                  i
                </button>
                <div className="tip">{t.help}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const WIN_TYPES = [
  ['top', '★', 'top pick', 'top_pick_won'],
  ['v', 'V', 'value', 'value_bet_won'],
  ['q', 'Q', 'quinella', 'quinella_hit'],
  ['t', 'T', 'trifecta', 'trifecta_hit'],
  ['f', 'F', 'first four', 'first_four_hit'],
  ['q4', 'Q4', 'quaddie', 'quaddie_hit'],
  ['eq', 'EQ', 'early quaddie', 'early_quaddie_hit'],
] as const;

/** What the bars and markers mean, with the sticky "Show on cards" switch once races have run. */
export function Legend({ showSwitch, on, onToggle }: { showSwitch: boolean; on: boolean; onToggle: () => void }) {
  return (
    <div className="ui-legend" data-tour="legend">
      {showSwitch && (
        <button type="button" className="twk" aria-pressed={on} onClick={onToggle}>
          <span className="knob" aria-hidden="true" />
          Show on cards
        </button>
      )}
      <span>
        <i className="ui-home-bar w" /> a tip won
      </span>
      <span>
        <i className="ui-home-bar l" /> no tip won
      </span>
      <span>
        <i className="ui-home-bar n" /> not run yet
      </span>
      {WIN_TYPES.map(([cls, sym, label]) => (
        <span key={cls}>
          <i className={`ui-home-wt ${cls}`}>{sym}</i> {label}
        </span>
      ))}
    </div>
  );
}

/** One meeting: name, conditions, result bars and returns, with one link to its tips page. */
export function MeetingCard({ meeting: m, locked = false, next, tour }: { meeting: HomeMeeting; locked?: boolean; next?: string; tour?: boolean }) {
  const s = meetingStats(m);
  const segs = m.raceResultSegments ?? [];
  const hasRun = s.run > 0;
  const track = useTrack();
  const abandoned = m.trackConditions?.abandoned === true;
  const lowStrike = m.track.performance?.top_1_win_percentage != null && m.track.performance.top_1_win_percentage < 26;
  return (
    <article
      className={`ui-mc${abandoned ? ' abandoned' : ''}${m.is_private ? ' private' : ''}${m.is_training ? ' training' : ''}`}
      data-tour={tour ? 'meeting' : undefined}
    >
      {(m.is_private || m.is_training) && (
        <div className="tags">
          {m.is_private && <span className="ptag">Private</span>}
          {m.is_training && <span className="ptag ttag">Training</span>}
        </div>
      )}
      <div className="hd">
        <div className="nm">
          <h3>
            {(m.is_private || locked) && <><LockIcon /> </>}
            {m.track.name}
          </h3>
          <span className="st">
            {m.track.state.toUpperCase()} · {titleCase(m.track.type)}
          </span>
        </div>
        {abandoned ? <span className="ab">Abandoned</span> : <GoingBadge condition={m.trackConditions?.trackCondition} />}
      </div>
      {lowStrike && <div className="low">⚠ Lower performing track - actively tuning</div>}
      {hasRun ? (
        <div className="kpis">
          <div className="kpi">
            <div className="kl">Top pick won</div>
            <div className="kv">
              {s.topWinners}/{s.topPicks}
            </div>
          </div>
          <div className="kpi">
            <div className="kl">Any tip won</div>
            <div className="kv">
              {s.anyWon}/{s.run}
            </div>
          </div>
        </div>
      ) : (
        <div className="kpis pre">
          <div className="kpi">
            <div className="kl">Races</div>
            <div className="kv">{m.race_count}</div>
          </div>
          <div className="kpi">
            <div className="kl">Next race</div>
            <div className="kv">{next ?? '—'}</div>
          </div>
        </div>
      )}
      {segs.length > 0 && (
        <div className="bars" style={{ gridTemplateColumns: `repeat(${segs.length}, 1fr)` }}>
          {segs.map((g) => {
            const cls = g.had_winner === true ? 'w' : g.had_winner === false ? 'l' : 'n';
            return (
              <div className="col" key={g.race_number}>
                <div className="wts">
                  {g.had_winner === true &&
                    WIN_TYPES.filter(([, , , k]) => g[k]).map(([c, sym, label]) => (
                      <span key={c} className={`ui-home-wt ${c}`} title={label}>
                        {sym}
                      </span>
                    ))}
                </div>
                <span
                  className={`fb ui-home-bar ${cls}`}
                  title={`Race ${g.race_number}: ${cls === 'w' ? 'a tip won' : cls === 'l' ? 'no tip won' : 'not run yet'}`}
                />
              </div>
            );
          })}
        </div>
      )}
      {hasRun && (
        <div className="kpis rois">
          <div className="kpi">
            <div className="kl">Top pick ROI</div>
            <div className="kv">{s.roi == null ? <span className="ip">In progress</span> : <Roi value={s.roi} />}</div>
          </div>
          {showValueRoi(s.valueRoi) && (
            <div className="kpi">
              <div className="kl">Value ROI</div>
              <div className="kv">
                <Roi value={s.valueRoi} />
              </div>
            </div>
          )}
        </div>
      )}
      <div className="acts">
        <Link
          to={meetingHref(m)}
          className={`ui-home-btn ${locked ? 'upgrade' : 'primary'}`}
          onClick={() => track('ui_click', { target: locked ? 'upgrade' : 'meeting_card', trackName: m.track.name, hasRun })}
        >
          {locked ? (
            <>
              <LockIcon /> Upgrade
            </>
          ) : hasRun ? (
            'Tips & results'
          ) : (
            'View tips'
          )}
        </Link>
      </div>
    </article>
  );
}

type Period = '7_DAYS' | '1_MONTH';

export interface TrackRecordData {
  topPickStatsByTrackType?: {
    byTrackType: { trackType: string; winners: number; races: number; winPercentage: number }[];
    totalWinners: number;
    totalRaces: number;
    overallWinPercentage: number;
  } | null;
  totalWinnersStatsByTrackType?: {
    byTrackType?: { trackType: string; winners: number; races: number; winPercentage: number }[];
    totalWinners: number;
    totalRaces: number;
  } | null;
  trackWithBestROI?: ({ trackName: string; winners: number; races: number; roi: number } & MeetingRef) | null;
  trackWithMostWinners?: { trackName: string; winners: number; races: number; winPercentage: number } | null;
  trackWithBestValueROI?: ({ trackName: string; winners: number; races: number; roi: number } & MeetingRef) | null;
  quaddieStats?: { successfulQuaddies: number; totalMeetingsAnalyzed: number; successPercentage: number } | null;
  earlyQuaddieStats?: { successfulEarlyQuaddies: number; totalMeetingsAnalyzed: number; successPercentage: number } | null;
  averageWinnerSP?: { averagePrice: number; totalWinners: number } | null;
  biggestWinner?: ({ horseName: string; trackName: string; date: string; price: number; raceNumber?: number | null } & MeetingRef) | null;
  consensusPickStats?: { rows: { trackType: string; agreeRaces: number; agreeWins: number; agreeStrikePct: number; m1StrikePct: number }[] } | null;
}

/** A track-record card's heading: icon, title and an optional explanation. */
function Head({ ic, cls, children, help }: { ic: string; cls: string; children: string; help?: string }) {
  return (
    <div className="h">
      <span className={`ic ${cls}`} aria-hidden="true">
        {ic}
      </span>
      {children}
      {help && <Help text={help} title={children} />}
    </div>
  );
}

/** Makes a whole track-record card a link to the meeting (or race) it is about, when the API says which. */
function Go({ to, label }: { to: string | null; label: string }) {
  return to ? <Link className="go" to={to} aria-label={label} /> : null;
}

const DOT: Record<string, string> = { Metropolitan: 'metro', Provincial: 'prov', Country: 'country' };
const TYPE_ORDER = ['metropolitan', 'provincial', 'country'];

interface SplitRow {
  trackType: string;
  winners: number;
  races: number;
  winPercentage: number;
}

/** Metropolitan / Provincial / Country lines under a track-record figure. */
function Splits({ rows }: { rows: SplitRow[] | undefined }) {
  const ordered = TYPE_ORDER.map((t) => (rows ?? []).find((r) => r.trackType.toLowerCase() === t)).filter((r): r is SplitRow => !!r && r.races > 0);
  if (!ordered.length) return null;
  return (
    <div className="bd">
      {ordered.map((r) => {
        const label = titleCase(r.trackType);
        return (
          <div key={label}>
            <span>
              <i className={`dot ${DOT[label] ?? ''}`} />
              {label}
            </span>
            <span>
              <b>{pct(r.winPercentage)}</b> <em>
                {r.winners}/{r.races}
              </em>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** "Our track record": the old marketing tiles over the last 7 days or month. Cards without data are left out. */
export function TrackRecord({ data, period, onPeriod }: { data: TrackRecordData | undefined; period: Period; onPeriod: (p: Period) => void }) {
  const { markName } = useBrand();
  const copy = useCopy();
  const top = data?.topPickStatsByTrackType;
  const any = data?.totalWinnersStatsByTrackType;
  const best = data?.trackWithBestROI;
  const most = data?.trackWithMostWinners;
  const value = data?.trackWithBestValueROI;
  const avg = data?.averageWinnerSP;
  const big = data?.biggestWinner;
  const q4 = data?.quaddieStats;
  const eq = data?.earlyQuaddieStats;
  const rows = data?.consensusPickStats?.rows ?? [];
  const agree = rows.find((r) => r.trackType === 'total');
  const agreeSplits = rows.map((r) => ({ trackType: r.trackType, winners: r.agreeWins, races: r.agreeRaces, winPercentage: r.agreeStrikePct }));
  const cards = [
    top && top.totalRaces > 0 && (
      <div className="tr" key="top">
        <Head ic="★" cls="gold" help="How often our number-one pick in a race wins.">
          Top pick win rate
        </Head>
        <div className="big">{pct(top.overallWinPercentage)}</div>
        <div className="sub">
          {top.totalWinners} winners from {top.totalRaces.toLocaleString('en-AU')} races
        </div>
        <Splits rows={top.byTrackType} />
      </div>
    ),
    agree && agree.agreeRaces > 0 && (
      <div className="tr" key="agree">
        <Head ic="★" cls="mark" help={`How often our ${markName} selections have won. All top picks won ${pct(agree.m1StrikePct)} over the same period.`}>
          {`${markName} win rate`}
        </Head>
        <div className="big">{pct(agree.agreeStrikePct)}</div>
        <div className="sub">
          {agree.agreeWins} winners from {agree.agreeRaces.toLocaleString('en-AU')} tips
        </div>
        <Splits rows={agreeSplits} />
      </div>
    ),
    most && most.races > 0 && (
      <div className="tr" key="most">
        <Head ic="♥" cls="plum" help="The track where our top picks won most often.">
          Top performing track
        </Head>
        <div className="name">{titleCase(most.trackName)}</div>
        <div className="big">{pct(most.winPercentage)}</div>
        <div className="sub">
          {plural(most.winners, 'winner')} from {most.races} races
        </div>
      </div>
    ),
    best && (
      <div className="tr" key="best">
        <Go to={meetingRefHref(best)} label={`See the ${titleCase(best.trackName)} meeting`} />
        <Head ic="$" cls="good" help="Return on $1 on every top pick at that one meeting. Tap the card to see the meeting.">
          Best meeting return
        </Head>
        <div className="name">{titleCase(best.trackName)}</div>
        <div className="big">
          <Roi value={best.roi * 100} />
        </div>
        <div className="sub">
          {plural(best.winners, 'winner')} from {best.races} races
        </div>
      </div>
    ),
    value && (
      <div className="tr" key="value">
        <Go to={meetingRefHref(value)} label={`See the ${titleCase(value.trackName)} meeting`} />
        <Head ic="↗" cls="blue" help="Return on $1 on every value bet at that one meeting. Tap the card to see the meeting.">
          Best value bet track
        </Head>
        <div className="name">{titleCase(value.trackName)}</div>
        <div className="big">
          <Roi value={value.roi * 100} />
        </div>
        <div className="sub">
          {plural(value.winners, 'winner')} from {value.races} races
        </div>
      </div>
    ),
    avg && avg.totalWinners > 0 && (
      <div className="tr" key="avg">
        <Head ic="◷" cls="rust" help="Mean starting price of our winning top picks.">
          Average winning price
        </Head>
        <div className="big">{money(avg.averagePrice)}</div>
        <div className="sub">Across {avg.totalWinners} winners</div>
      </div>
    ),
    any && any.totalRaces > 0 && (
      <div className="tr" key="any">
        <Head ic="≡" cls="teal" help="Races where at least one of our tips won.">
          Any tip won
        </Head>
        <div className="big">{pct(share(any.totalWinners, any.totalRaces))}</div>
        <div className="sub">
          {any.totalWinners} of {any.totalRaces.toLocaleString('en-AU')} races
        </div>
        <Splits rows={any.byTrackType} />
      </div>
    ),
    big && (
      <div className="tr" key="big">
        <Go to={meetingRefHref(big, big.raceNumber)} label={`See ${big.horseName}'s race at ${titleCase(big.trackName)}`} />
        <Head ic="▲" cls="gold" help="The longest-priced winner among our top picks in this period. Tap the card to see the race.">
          Biggest winner
        </Head>
        <div className="name">{big.horseName}</div>
        <div className="big up">{money(big.price)}</div>
        <div className="sub">
          {titleCase(big.trackName)} · {big.date}
        </div>
      </div>
    ),
    q4 && q4.totalMeetingsAnalyzed > 0 && (
      <div className="tr" key="q4">
        <Head ic="Q4" cls="plum" help="Meetings where one of our tips won each of the last four races.">
          Quaddie
        </Head>
        <div className="big">{pct(q4.successPercentage)}</div>
        <div className="sub">
          {q4.successfulQuaddies} from {q4.totalMeetingsAnalyzed} meetings
        </div>
      </div>
    ),
    eq && eq.totalMeetingsAnalyzed > 0 && (
      <div className="tr" key="eq">
        <Head ic="EQ" cls="rust" help="Meetings where one of our tips won each early quaddie leg.">
          Early quaddie
        </Head>
        <div className="big">{pct(eq.successPercentage)}</div>
        <div className="sub">
          {eq.successfulEarlyQuaddies} from {eq.totalMeetingsAnalyzed} meetings
        </div>
      </div>
    ),
  ].filter(Boolean);
  return (
    <section className="ui-trk" data-tour="record">
      <div className="ui-home-sh">
        <h2>{copy.trackRecord}</h2>
        <Seg
          label="Period"
          value={period}
          onChange={onPeriod}
          options={[
            { value: '7_DAYS', label: '7 days' },
            { value: '1_MONTH', label: '1 month' },
          ]}
        />
      </div>
      {cards.length ? <div className="trg">{cards}</div> : <p className="ui-home-muted">{copy.noTrackRecord}</p>}
      <p className="foot">
        Return (ROI) is what you would get back if you put $1 on every race's top pick, minus the $1. It is worked out at starting price.
      </p>
    </section>
  );
}

const GROUPS = [
  ['metropolitan', 'Metropolitan'],
  ['provincial', 'Provincial'],
  ['country', 'Country'],
] as const;

/** Meetings under Metropolitan / Provincial / Country, each group with its pooled ROI (home page and the preview). */
export function MeetingGroups({
  meetings,
  isLocked = () => false,
  nextRace = () => undefined,
  tour = false,
}: {
  meetings: HomeMeeting[];
  isLocked?: (m: HomeMeeting) => boolean;
  nextRace?: (m: HomeMeeting) => string | undefined;
  /** Mark the first group and card for the page tour. */
  tour?: boolean;
}) {
  const grouped = groupMeetings(meetings);
  const firstGroup = GROUPS.findIndex(([k]) => grouped[k].length);
  let firstCard = tour;
  return (
    <>
      {GROUPS.map(([key, label], gi) => {
        const ms = grouped[key];
        if (!ms.length) return null;
        const run = ms.some((m) => racesRun(m) > 0);
        const v = categoryValueRoi(ms);
        return (
          <div className="ui-grp" key={key}>
            <div className="ui-gh" data-tour={tour && gi === firstGroup ? 'groups' : undefined}>
              <h3>{label}</h3>
              {run && (
                <span className="rois">
                  <span>
                    <i>
                      Top Pick ROI<em>:</em>
                    </i>{' '}
                    <Roi value={categoryRoi(ms)} />
                  </span>
                  {showValueRoi(v) && (
                    <span>
                      <i>
                        Value Bet ROI<em>:</em>
                      </i>{' '}
                      <Roi value={v} />
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="ui-mcs">
              {ms.map((m) => {
                const markTour = firstCard;
                firstCard = false;
                return <MeetingCard key={m.meeting_id} meeting={m} locked={isLocked(m)} next={nextRace(m)} tour={markTour} />;
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}

/**
 * A day whose tips aren't out yet. With `tracks`, the meetings whose tips are written but not
 * yet released ("coming soon"); without, a plain note that tips come out the night before.
 * `more` when some of the day's meetings are already showing.
 */
export function TipsComingSoon({ date, tracks, more = false }: { date: string; tracks?: string[]; more?: boolean }) {
  const copy = useCopy();
  const soon = !!tracks?.length;
  return (
    <section className="ui-soon" role="status">
      <BrandMascot mood={soon ? 'excited' : 'relaxed'} size={72} />
      <div>
        <b>{soon ? (more ? copy.moreTipsComingSoon : copy.tipsComingSoon) : copy.noTipsForDayYet}</b>
        {soon && (
          <>
            <div className="ui-home-muted">
              {plural(tracks.length, 'meeting')} · {shortDay(date)}
            </div>
            <ul className="trks">
              {tracks.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </>
        )}
        <p className="ui-home-muted">{copy.tipsReleaseNote}</p>
      </div>
    </section>
  );
}
